import React from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale
);

const AnomalyScatterChart = ({ title, anomalyData, yAxisLabel, timeRange = "24h" }) => {
  // Time range configurations for X-axis (same as other charts)
  const getTimeRangeConfig = (range) => {
    const now = new Date();
    
    // Handle custom range objects
    if (typeof range === 'object' && range.type === 'custom') {
      const fromDate = new Date(range.from.endsWith('Z') ? range.from : range.from + 'Z');
      const toDate = new Date(range.to.endsWith('Z') ? range.to : range.to + 'Z');
      const diffMs = toDate.getTime() - fromDate.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      
      // Configure based on custom range duration
      if (diffDays <= 1) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'hour',
          stepSize: 2,
          displayFormat: 'HH:mm'
        };
      } else if (diffDays <= 7) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'day',
          stepSize: 1,
          displayFormat: 'MMM dd HH:mm'
        };
      } else if (diffDays <= 90) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'day',
          stepSize: Math.ceil(diffDays / 10),
          displayFormat: 'MMM dd'
        };
      } else {
        return {
          min: fromDate,
          max: toDate,
          unit: 'month',
          stepSize: 1,
          displayFormat: 'MMM yyyy'
        };
      }
    }
    
    // Handle preset string ranges
    const configs = {
      '5m': {
        min: new Date(now.getTime() - 5 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 1,
        displayFormat: 'HH:mm:ss'
      },
      '15m': {
        min: new Date(now.getTime() - 15 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 2,
        displayFormat: 'HH:mm'
      },
      '30m': {
        min: new Date(now.getTime() - 30 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 5,
        displayFormat: 'HH:mm'
      },
      '1h': {
        min: new Date(now.getTime() - 1 * 60 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 10,
        displayFormat: 'HH:mm'
      },
      '3h': {
        min: new Date(now.getTime() - 3 * 60 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 30,
        displayFormat: 'HH:mm'
      },
      '6h': {
        min: new Date(now.getTime() - 6 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 1,
        displayFormat: 'HH:mm'
      },
      '12h': {
        min: new Date(now.getTime() - 12 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 2,
        displayFormat: 'HH:mm'
      },
      '24h': {
        min: new Date(now.getTime() - 24 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 3,
        displayFormat: 'HH:mm'
      },
      '2d': {
        min: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 6,
        displayFormat: 'MMM dd HH:mm'
      },
      '7d': {
        min: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 1,
        displayFormat: 'MMM dd'
      },
      '14d': {
        min: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 1,
        displayFormat: 'MMM dd'
      },
      '30d': {
        min: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 3,
        displayFormat: 'MMM dd'
      },
      '90d': {
        min: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 7,
        displayFormat: 'MMM dd'
      }
    };
    return configs[range] || configs['24h'];
  };

  const timeConfig = getTimeRangeConfig(timeRange);

  // Handle both old single dataset format and new multi-series format
  const isMultiSeries = anomalyData && anomalyData.rsrpTimeSeries;
  
  let chartData;
  
  if (isMultiSeries) {
    // New format: multiple series for RSRP, RSRQ, SINR
    const datasets = [];
    
    if (anomalyData.rsrpTimeSeries && anomalyData.rsrpTimeSeries.length > 0) {
      datasets.push({
        label: 'RSRP',
        data: anomalyData.rsrpTimeSeries.map(point => ({
          x: point.timestamp,
          y: point.anomalousValue
        })),
        backgroundColor: '#3498db',
        borderColor: '#3498db',
        pointRadius: 5,
        pointHoverRadius: 7,
        showLine: false,
      });
    }
    
    if (anomalyData.rsrqTimeSeries && anomalyData.rsrqTimeSeries.length > 0) {
      datasets.push({
        label: 'RSRQ',
        data: anomalyData.rsrqTimeSeries.map(point => ({
          x: point.timestamp,
          y: point.anomalousValue
        })),
        backgroundColor: '#e74c3c',
        borderColor: '#e74c3c',
        pointRadius: 5,
        pointHoverRadius: 7,
        showLine: false,
      });
    }
    
    if (anomalyData.sinrTimeSeries && anomalyData.sinrTimeSeries.length > 0) {
      datasets.push({
        label: 'SINR',
        data: anomalyData.sinrTimeSeries.map(point => ({
          x: point.timestamp,
          y: point.anomalousValue
        })),
        backgroundColor: '#2ecc71',
        borderColor: '#2ecc71',
        pointRadius: 5,
        pointHoverRadius: 7,
        showLine: false,
      });
    }
    
    chartData = { datasets };
  } else {
    // Old format: single dataset (backward compatibility)
    chartData = {
      datasets: [{
        label: 'Anomalous Values',
        data: (anomalyData || []).map(point => ({
          x: point.timestamp,
          y: point.value
        })),
        backgroundColor: '#e74c3c',
        borderColor: '#e74c3c',
        pointRadius: 5,
        pointHoverRadius: 7,
        showLine: false,
      }]
    };
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'point',
      intersect: false,
    },
    plugins: {
      legend: {
        display: isMultiSeries, // Show legend for multi-series, hide for single
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            const date = new Date(context[0].parsed.x);
            return date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
          },
          label: function(context) {
            if (isMultiSeries) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}`;
            } else {
              // Old format compatibility
              const dataPoint = anomalyData[context.dataIndex];
              return [
                `${yAxisLabel}: ${context.parsed.y.toFixed(2)}`,
                `RSRQ: ${dataPoint?.anomalous_rsrq_db || 'N/A'} dB`,
                `SINR: ${dataPoint?.anomalous_sinr_db || 'N/A'} dB`
              ];
            }
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        min: timeConfig.min,
        max: timeConfig.max,
        bounds: 'data',
        time: {
          unit: timeConfig.unit,
          stepSize: timeConfig.stepSize,
          displayFormats: {
            minute: timeConfig.displayFormat,
            hour: timeConfig.displayFormat,
            day: timeConfig.displayFormat
          }
        },
        ticks: {
          callback: function(value) {
            const d = new Date(value);
            const fmt = timeConfig.displayFormat;
            if (fmt === 'HH:mm:ss') return d.getUTCHours().toString().padStart(2,'0') + ':' + d.getUTCMinutes().toString().padStart(2,'0') + ':' + d.getUTCSeconds().toString().padStart(2,'0');
            if (fmt === 'HH:mm') return d.getUTCHours().toString().padStart(2,'0') + ':' + d.getUTCMinutes().toString().padStart(2,'0');
            if (fmt === 'MMM dd HH:mm') return d.toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
            if (fmt === 'MMM dd') return d.toLocaleString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' });
            if (fmt === 'MMM yyyy') return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
            return value;
          }
        },
        title: {
          display: true,
          text: 'Time (UTC)'
        },
        grid: {
          color: '#e0e0e0'
        }
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: yAxisLabel || 'Value'
        },
        grid: {
          color: '#e0e0e0'
        }
      }
    }
  };

  return (
    <div className="chart-container-wrapper">
      <h4 className="chart-title">{title}</h4>
      <div className="chart-canvas-container">
        <Scatter data={chartData} options={options} />
      </div>
    </div>
  );
};

export default AnomalyScatterChart;
